// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.
import React, { useState, useEffect } from "react";
import Header from "../../component/_elements/Header.tsx";
import { useOutletContext } from "react-router-dom";
import { OutletContextType } from "../../types/GlobalType.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  messageControler,
  MessageType,
} from "../../_api/controllers/message.controller.ts";
import moment from "moment";
import { capitalizeFirstLetter } from "../../_services/utils.jsx";
import { studentControler } from "../../_api/controllers/student.controller.ts";
import {
  classeController,
  promotionController,
  schoolYearController,
} from "../../_api/controllers/scolarite.controller.ts";
import { personalController } from "../../_api/controllers/personal.controller.ts";
import { parentController } from "../../_api/controllers/parent.controller.ts";
import { EHULA_CONTACT } from "../../App.constants.js";
import MyLoader from "../../component/loader/MyLoader.tsx";

// skeleton component for messages management loading state
const MessagesManagementSkeleton: React.FC<{
  isDarkMode: boolean;
}> = ({ isDarkMode }) => (
  <main className="ml-20 md:ml-0 p-6 mt-16 animate-pulse">
    {/* Header */}
    <div className="mb-6 flex justify-between items-center">
      <div>
        <div
          className={`h-6 w-56 rounded mb-2 ${
            isDarkMode ? "bg-gray-700" : "bg-gray-300"
          }`}
        ></div>
        <div
          className={`h-4 w-80 rounded ${
            isDarkMode ? "bg-gray-600" : "bg-gray-200"
          }`}
        ></div>
      </div>
      <div className="flex gap-3"></div>
    </div>

    {/* Search & Filters */}
    <div className="flex flex-wrap gap-4 mb-6">
      <div
        className={`flex-grow h-10 rounded-md ${
          isDarkMode ? "bg-gray-700" : "bg-gray-300"
        }`}
      ></div>
      <div
        className={`h-10 w-48 rounded-md ${
          isDarkMode ? "bg-gray-700" : "bg-gray-300"
        }`}
      ></div>
      <div
        className={`h-10 w-48 rounded-md ${
          isDarkMode ? "bg-gray-700" : "bg-gray-300"
        }`}
      ></div>
    </div>

    {/* TABLE SKELETON */}
    <div
      className={`rounded-lg overflow-hidden border mb-6 ${
        isDarkMode ? "border-gray-700" : "border-gray-300"
      }`}
    >
      <div className="w-full overflow-x-auto">
        <table className="min-w-full">
          <thead className={isDarkMode ? "bg-[#161B22]" : "bg-gray-200"}>
            <tr>
              {[...Array(6)].map((_, i) => (
                <th key={i} className="px-6 py-4">
                  <div
                    className={`h-4 rounded w-full ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-300"
                    }`}
                  ></div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody
            className={`divide-y ${
              isDarkMode
                ? "bg-[#0D1117] divide-gray-700"
                : "bg-white divide-gray-200"
            }`}
          >
            {[...Array(10)].map((_, row) => (
              <tr key={row}>
                {[...Array(6)].map((_, col) => (
                  <td key={col} className="px-6 py-4">
                    <div
                      className={`h-4 rounded w-full ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}
                    ></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </main>
);

const Message: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();
  const [currentYear, setCurrentYear] = useState("2025 - 2026");
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [showViewMessageModal, setShowViewMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestinateur, setSelectedDestinateur] = useState(
    "Tous les destinateurs",
  );
  const [selectedCategory, setSelectedCategory] = useState("Tous");
  const [selectedType, setSelectedType] = useState("Tous les types");
  const [selectedStatus, setSelectedStatus] = useState("Tous les statuts");
  const [isClassDropdownOpen, setIsDestinateurDropdownOpen] = useState(false);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  // const [showEditMessageModal, setShowEditMessageModal] = useState(false);
  // const [messageToEdit, setMessageToEdit] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<any>(null);

  const [notification, setNotification] = useState<any>(null); // { message, type }
  // Fonction pour afficher une notification
  const showNotification = (message: string, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const [createMessageForm, setcreateMessageForm] = useState<{
    objet: any;
    contenu: string;
    type_destinateur:
      | "parents"
      | "parents_class"
      | "single_parent"
      | "personals"
      | "single_personal"
      | string;
    destinateur_id: any;
    annee_scolaire_id?: any;
    type_diffuison?: any | "individual" | "groupe";
    canal: "email" | "sms";
    type_request:
      | any
      | "sms_all_parents"
      | "sms_parents_class"
      | "sms_one_parent"
      | "email_all_parents"
      | "email_parents_class"
      | "email_one_parent"
      | "sms_all_personals"
      | "sms_one_personal"
      | "email_all_personals"
      | "email_one_personal";

    status: any;
  }>({
    objet: "",
    contenu: "",
    type_destinateur: "parents_class",
    destinateur_id: "",
    annee_scolaire_id: "",
    type_diffuison: "",
    canal: "email",
    type_request: null,
    status: 0,
  });

  // Gère le changement de canal d'envoi. Le contenu reste toujours du texte brut ;
  // on tronque simplement à 320 caractères si on bascule vers le SMS.
  const handleCanalChange = (value: "email" | "sms") => {
    setcreateMessageForm((prev) => ({
      ...prev,
      canal: value,
      contenu: value === "sms" ? prev.contenu.substring(0, 320) : prev.contenu,
    }));
  };

  const getRequestType = ():
    | "sms_all_parents"
    | "sms_parents_class"
    | "sms_one_parent"
    | "email_all_parents"
    | "email_parents_class"
    | "email_one_parent"
    | "sms_all_personals"
    | "sms_one_personal"
    | "email_all_personals"
    | "email_one_personal" => {
    let request_name: any = null;

    switch (createMessageForm.canal) {
      case "sms":
        switch (createMessageForm.type_destinateur) {
          case "parents":
            if (createMessageForm.destinateur_id === "all") {
              request_name = "sms_all_parents";
            }
            return request_name;
          case "parents_class":
            if (
              createMessageForm.destinateur_id !== "all" &&
              createMessageForm.destinateur_id !== ""
            ) {
              request_name = "sms_parents_class";
            }
            return request_name;
          case "single_parent":
            if (createMessageForm.destinateur_id !== "") {
              request_name = "sms_one_parent";
            }
            return request_name;
          case "personals":
            if (
              createMessageForm.destinateur_id === "all" &&
              createMessageForm.destinateur_id !== ""
            ) {
              request_name = "sms_all_personals";
            }
            return request_name;
          case "single_personal":
            if (
              createMessageForm.destinateur_id !== "" &&
              createMessageForm.destinateur_id !== "all"
            ) {
              request_name = "sms_one_personal";
            }
            return request_name;
          default:
            break;
        }
        break;
      case "email":
        switch (createMessageForm.type_destinateur) {
          case "parents":
            if (createMessageForm.destinateur_id === "all") {
              request_name = "email_all_parents";
            }
            break;
          case "parents_class":
            if (
              createMessageForm.destinateur_id !== "all" &&
              createMessageForm.destinateur_id !== ""
            ) {
              request_name = "email_parents_class";
            }
            break;
          case "single_parent":
            if (createMessageForm.destinateur_id !== "") {
              request_name = "email_one_parent";
            }
            break;
          case "personals":
            if (
              createMessageForm.destinateur_id === "all" &&
              createMessageForm.destinateur_id !== ""
            ) {
              request_name = "email_all_personals";
            }
            break;
          case "single_personal":
            if (
              createMessageForm.destinateur_id !== "" &&
              createMessageForm.destinateur_id !== "all"
            ) {
              request_name = "email_one_personal";
            }
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }

    return request_name;
  };
  //get The request name dependant of url send message
  getRequestType();

  //fetch all messages
  const { data: messagesData, isLoading: isMessagesLoading } = useQuery({
    queryKey: ["messages"],
    queryFn: messageControler.getAll,
  });
  //fetch eleves
  const { data: elevesData, isLoading: isElevesLoading } = useQuery({
    queryKey: ["students"],
    queryFn: studentControler.getAll,
  });

  //fetch classe
  const { data: classesData, isLoading: isClassesLoading } = useQuery({
    queryKey: ["classes"],
    queryFn: classeController.getAll,
  });

  const { data: promotionData, isLoading: isPromotionsLoading } = useQuery({
    queryKey: ["promotions"],
    queryFn: promotionController.getAll,
  });

  //fetch active schoolYear scolaire
  const { data: schoolYearActiveData, isLoading: isSchoolYearActiveLoading } =
    useQuery({
      queryKey: ["activeSchoolYears"],
      queryFn: schoolYearController.getActive,
    });

  const { data: schoolYear } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });

  //fetch all Personals
  const { data: personalsData, isLoading: isPersonalsLoading } = useQuery({
    queryKey: ["personals"],
    queryFn: personalController.getAll,
  });
  //fetch all parents
  const { data: parentsData, isLoading: isParentsLoading } = useQuery({
    queryKey: ["parents"],
    queryFn: parentController.getAll,
  });

  const messagesArray = messagesData?.data ? messagesData.data : [];
  const studentsArray = elevesData?.data ? elevesData.data : [];
  const classesArray = classesData?.data ? classesData.data : [];
  const promotionsArray = promotionData?.data ? promotionData.data : [];
  const personalsArray = personalsData?.data ? personalsData.data : [];
  const parentsArray = parentsData?.data ? parentsData.data : [];
  const schoolYearActiveDataArr = schoolYearActiveData?.data
    ? schoolYearActiveData.data
    : [];

  const schoolYearArr = schoolYear?.data || [];
  const selectedSchoolYear = schoolYearArr.find((sy: any) => sy.selected);

  //prepare affected students data
  const affectedStudents = studentsArray.map((student) => {
    let classeDesignation = "",
      parentEmail = "",
      parentNoms = "",
      parentPhone = "",
      parentId: any = "",
      promotionData: any = "",
      classeId: any = "";

    for (const promo of promotionsArray) {
      if (
        promo.eleve_id === student.eleve_id &&
        selectedSchoolYear?.id === promo.annee_scolaire_id
      ) {
        promotionData = promo; //filter the promotion of the student for the active school year
        for (const parent of parentsArray) {
          if (parent.parent_id === student.parent_id) {
            parentNoms = `${capitalizeFirstLetter(parent.nom)} ${capitalizeFirstLetter(parent.postnom)} ${capitalizeFirstLetter(parent.prenom)}`;
            parentEmail = parent?.email || EHULA_CONTACT.EMAIL;
            parentPhone = parent.telephone || EHULA_CONTACT.PHONE;
            parentId = parent.parent_id;
            //Affecter
          }
        }
      }
    }

    for (const classe of classesArray) {
      if (promotionData?.classe_id === classe.id) {
        classeDesignation = classe.designation;
        classeId = classe.id;
      }
    }

    return {
      noms: `${capitalizeFirstLetter(student.nom)} ${capitalizeFirstLetter(student.postnom)} ${capitalizeFirstLetter(
        student.prenom,
      )}`,
      classeDesignation,
      nom: student.nom,
      postnom: student.postnom,
      prenom: student.prenom,
      classeId,
      parentId,
      parentEmail,
      parentPhone,
      parentNoms,
    };
  });

  console.log("affectedStudents");
  console.log(affectedStudents);
  console.log(createMessageForm);

  const affectedPersonals = personalsArray.map((personal) => {
    return {
      personalId: personal.personnel_id,
      noms: `${personal.nom} ${personal.postnom} ${capitalizeFirstLetter(
        personal.prenom,
      )}`,
      email: personal.email || EHULA_CONTACT.EMAIL,
      phone: personal.telephone || EHULA_CONTACT.PHONE,
    };
  });

  const messagesPerPage = 12;
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  const toggleYearDropdown = () => {
    setIsYearDropdownOpen(!isYearDropdownOpen);
  };

  const completeMessage = messagesArray.map((message: MessageType, index) => {
    const canalType =
      message.canal_mail === 1
        ? "Email"
        : message.canal_sms === 1
          ? "SMS"
          : message.canal_notification === 1
            ? "Soft SMS"
            : "Unset";

    const destinataireType =
      message.type_destinateur_id === 1 ? "Parent" : "Professeur";

    const classe =
      message.type_diffusion === "groupe" ? "Groupe" : "Individuel";

    let destinataireNom: string | null = null;
    let destinataireContact: string | null = null;

    // Résolution uniquement pour les messages individuels
    if (classe === "Individuel") {
      if (destinataireType === "Parent") {
        const parent = parentsArray.find(
          (item) => Number(item.parent_id) === Number(message.destinateur_id),
        );

        if (parent) {
          destinataireNom =
            `${parent.nom || ""} ${parent.postnom || ""} ${parent.prenom || ""}`.trim();

          destinataireContact =
            canalType === "SMS"
              ? parent.telephone || null
              : canalType === "Email"
                ? parent.email || null
                : null;
        }
      }

      if (destinataireType === "Professeur") {
        const personnel = personalsArray.find(
          (item) =>
            Number(item.personnel_id) === Number(message.destinateur_id),
        );

        if (personnel) {
          destinataireNom =
            `${personnel.nom || ""} ${personnel.postnom || ""} ${personnel.prenom || ""}`.trim();

          destinataireContact =
            canalType === "SMS"
              ? personnel.telephone || null
              : canalType === "Email"
                ? personnel.email || null
                : null;
        }
      }
    }

    return {
      id: message.id,
      date: moment(message.created_at).format("DD/MM/YYYY"),
      heure: moment(message.created_at).format("HH:mm"),
      objet: capitalizeFirstLetter(message.objet),
      contenu: message.contenu,
      destinateur_id: message.destinateur_id,

      destinataires: destinataireType,
      classe,
      type: canalType,

      // nouvelles informations
      destinataireNom,
      destinataireContact,

      statut: message.status === 1 ? "Envoyé" : "Brouillon",

      canal:
        message.canal_mail === 1
          ? "Email"
          : message.canal_sms === 1
            ? "SMS"
            : "Notification",
    };
  });

  // console.log(completeMessage);
  // Données des communications
  const communications: any[] = [...completeMessage];

  // Filtrer les communications selon la recherche et les filtres
  const filteredCommunications = communications.filter((communication) => {
    const matchesSearch =
      communication.objet.toLowerCase().includes(searchQuery.toLowerCase()) ||
      communication.contenu.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDestinateur =
      selectedDestinateur === "Tous les destinateurs" ||
      communication.destinataires === selectedDestinateur;
    const matchesType =
      selectedType === "Tous les types" || communication.type === selectedType;
    const matchesStatus =
      selectedStatus === "Tous les statuts" ||
      communication.statut === selectedStatus;

    const matchesCategory =
      selectedCategory === "Tous" || communication.classe === selectedCategory;

    return (
      matchesSearch &&
      matchesDestinateur &&
      matchesType &&
      matchesStatus &&
      matchesCategory
    );
  });
  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = filteredCommunications.slice(
    indexOfFirstMessage,
    indexOfLastMessage,
  );

  // console.log(currentMessages);
  const totalPages = Math.ceil(filteredCommunications.length / messagesPerPage);
  const handlePageChange = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleChangeDestinateur = (e: React.ChangeEvent<HTMLInputElement>) => {
    setcreateMessageForm((prev) => ({
      ...prev,
      type_destinateur: e.target.value,
      destinateur_id: "",
    }));
  };

  // console.log(createMessageForm.type_destinateur);
  // console.log(createMessageForm.destinateur_id);
  // console.log(getRequestType());

  const handleResetFilter = () => {
    setSelectedType("Tous les types");
    setSelectedStatus("Tous les statuts");
    setSelectedDestinateur("Tous les destinateurs");
  };

  //Handle mouse click outside for dropdowns
  const destinateurDropdownRef = React.useRef<HTMLDivElement>(null);
  const typeDropdownRef = React.useRef<HTMLDivElement>(null);
  const statusDropdownRef = React.useRef<HTMLDivElement>(null);
  // Fermeture des menus lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //export button
      if (
        destinateurDropdownRef.current &&
        !destinateurDropdownRef.current.contains(event.target as Node)
      ) {
        setIsDestinateurDropdownOpen(false);
      }

      //class button
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsTypeDropdownOpen(false);
      }

      //status button
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  //Mutation pour envoyer
  const queryClient = useQueryClient();

  //NOT DONE SEE THE BACK-END
  const mutationsendEMAILtoAllParents = useMutation({
    mutationFn: () =>
      messageControler.sendEMAILtoAllParents({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
      }),

    onSuccess: (data) => {
      // console.log(data);
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });
  //NOT DONE SEE THE BACK-END
  const mutationsendEMAILtoAllPersonals = useMutation({
    mutationFn: () =>
      messageControler.sendEMAILtoAllPersonals({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
      }),

    onSuccess: (data) => {
      // console.log(data);
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });
  //NOT DONE SEE THE BACK-END
  const mutationsendSMStoAllParents = useMutation({
    mutationFn: () =>
      messageControler.sendSMStoAllParents({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
      }),

    onSuccess: (data) => {
      // console.log(data);
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });
  //NOT DONE SEE THE BACK-END
  const mutationSMStoAllPersonals = useMutation({
    mutationFn: () =>
      messageControler.sendSMStoAllPersonals({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
      }),

    onSuccess: (data) => {
      // console.log(data);
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });
  //NOT DONE SEE THE BACK-END
  const mutationsendEMAILtoParentsByClass = useMutation({
    mutationFn: () =>
      messageControler.sendEMAILtoParentsByClass({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
        classe_id: createMessageForm.destinateur_id,
        annee_scolaire_id: schoolYearActiveDataArr[0]?.id,
      }),

    onSuccess: (data) => {
      // console.log(data);
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });
  //NOT DONE SEE THE BACK-END
  const mutationsendSMStoParentsByClass = useMutation({
    mutationFn: () =>
      messageControler.sendSMStoParentsByClass({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
        classe_id: createMessageForm.destinateur_id,
        annee_scolaire_id: schoolYearActiveDataArr[0]?.id,
      }),

    onSuccess: (data) => {
      // console.log(data);
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });
  //NOT DONE SEE THE BACK-END
  const mutationsendSMStoOneParent = useMutation({
    mutationFn: () =>
      messageControler.sendSMStoOneParent({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
        parent_id: createMessageForm.destinateur_id,
      }),

    onSuccess: (data) => {
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });
  //NOT DONE SEE THE BACK-END
  const mutationsendSMStoOnePersonal = useMutation({
    mutationFn: () =>
      messageControler.sendSMStoOnePersonal({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
        personnel_id: createMessageForm.destinateur_id,
      }),

    onSuccess: () => {
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });
  //Done
  const mutationsendEMAILtoOneParent = useMutation({
    mutationFn: () =>
      messageControler.sendEMAILtoOneParent({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
        parent_id: createMessageForm.destinateur_id,
      }),

    onSuccess: () => {
      // console.log(data);
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });
  //DONE
  const mutationsendEMAILtoOnePersonal = useMutation({
    mutationFn: () =>
      messageControler.sendEMAILtoOnePersonal({
        objet: createMessageForm.objet,
        message: createMessageForm.contenu,
        personnel_id: createMessageForm.destinateur_id,
      }),

    onSuccess: (data) => {
      // console.log(data);
      setShowNewMessageModal(false);
      setcreateMessageForm({
        objet: "",
        contenu: "",
        type_destinateur: "parents_class",
        destinateur_id: "",
        annee_scolaire_id: "",
        type_diffuison: "",
        canal: "email",
        type_request: null,
        status: 0,
      });
      showNotification("Message envoyé avec succès", "success");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur s'est produite, réessayer", "erreur");
    },
  });

  //Mutation pour supprimer
  const mutationDeleteMessage = useMutation({
    mutationFn: () => messageControler.deleteMessage(messageToDelete.id),
    onSuccess: (data) => {
      // console.log("desactivate Mutation", data);
      setShowDeleteModal(false);
      showNotification(" Message supprimé avec succès!", "success");

      setMessageToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification(" Une erreur est survenue, réessayer!", "erreur");
    },
  });

  const handleSendMessage = () => {
    console.log("MMMMMMMMMMMMM");
    console.log(createMessageForm);
    if (
      !createMessageForm.objet ||
      !createMessageForm.contenu ||
      !createMessageForm.destinateur_id
    )
      return showNotification("Tous les champs sont obligatoire", "erreur");

    switch (getRequestType()) {
      case "sms_all_parents":
        mutationsendSMStoAllParents.mutate();
        break;
      case "email_all_parents":
        mutationsendEMAILtoAllParents.mutate();
        break;
      case "sms_parents_class":
        mutationsendSMStoParentsByClass.mutate();
        break;
      case "email_parents_class":
        mutationsendEMAILtoParentsByClass.mutate();
        break;
      case "sms_one_parent":
        mutationsendSMStoOneParent.mutate();
        break;
      case "email_one_parent":
        mutationsendEMAILtoOneParent.mutate();
        break;
      case "sms_all_personals":
        mutationSMStoAllPersonals.mutate();
        break;
      case "email_all_personals":
        mutationsendEMAILtoAllPersonals.mutate();
        break;
      case "sms_one_personal":
        mutationsendSMStoOnePersonal.mutate();
        break;
      case "email_one_personal":
        mutationsendEMAILtoOnePersonal.mutate();
        break;

      default:
        break;
    }
  };

  // function to cancel payment
  const handleDeleteMessage = () => {
    mutationDeleteMessage.mutate();
  };
  const isPending =
    mutationsendSMStoAllParents.isPending ||
    mutationsendSMStoParentsByClass.isPending ||
    mutationsendSMStoOneParent.isPending ||
    mutationsendEMAILtoAllParents.isPending ||
    mutationsendEMAILtoParentsByClass.isPending ||
    mutationsendEMAILtoOneParent.isPending ||
    mutationSMStoAllPersonals.isPending ||
    mutationsendSMStoOnePersonal.isPending ||
    mutationsendEMAILtoAllPersonals.isPending ||
    mutationsendEMAILtoOnePersonal.isPending;

  // Réinitialise complètement le formulaire de nouveau communiqué
  const resetCreateMessageForm = () => {
    setcreateMessageForm({
      objet: "",
      contenu: "",
      type_destinateur: "parents_class",
      destinateur_id: "",
      annee_scolaire_id: "",
      type_diffuison: "",
      canal: "email",
      type_request: null,
      status: 0,
    });
  };

  const toolbarButtonClass = `h-7 w-7 flex items-center justify-center rounded opacity-50 cursor-not-allowed ${
    isDarkMode ? "text-gray-300" : "text-gray-600"
  } !rounded-button whitespace-nowrap`;

  return (
    <div className="md:ml-60 w-full">
      <MyLoader isLoading={isPending} message="Envoie en cours..." />
      {/* Header */}
      <Header
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        toggleDarkMode={toggleDarkMode}
        currentYear={currentYear}
        isYearDropdownOpen={isYearDropdownOpen}
        toggleYearDropdown={toggleYearDropdown}
        setCurrentYear={setCurrentYear}
        setIsYearDropdownOpen={setIsYearDropdownOpen}
      />
      {/* Communications Management Content */}

      {isParentsLoading ||
      isPersonalsLoading ||
      isMessagesLoading ||
      isClassesLoading ||
      isPromotionsLoading ||
      isElevesLoading ||
      isSchoolYearActiveLoading ? (
        <MessagesManagementSkeleton isDarkMode={isDarkMode} />
      ) : (
        <main className="ml-20 md:ml-0 p-4 sm:p-6 mt-16">
          {/* ================= PAGE HEADER ================= */}
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  isDarkMode
                    ? "bg-blue-500/10 text-blue-400"
                    : "bg-blue-50 text-blue-600"
                }`}
              >
                <i className="fas fa-comments text-sm" />
              </div>

              <div>
                <h1
                  className={`text-sm font-semibold sm:text-base ${
                    isDarkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  Gestion des Communications
                </h1>

                <p
                  className={`mt-0.5 text-[10px] sm:text-[11px] ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Gérez les messages et communications avec les parents d'élèves
                </p>
              </div>
            </div>

            {/* Nouveau communiqué */}
            <button
              onClick={() => setShowNewMessageModal(true)}
              className="flex h-10 items-center justify-center gap-2 self-start rounded-lg bg-blue-600 px-4 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 lg:self-auto"
            >
              <i className="fas fa-plus text-[10px]" />
              <span>Nouveau Communiqué</span>
            </button>
          </div>

          {/* ================= MESSAGES CONTAINER ================= */}
          <div
            className={`overflow-hidden rounded-xl border ${
              isDarkMode
                ? "border-[#30363D] bg-[#0D1117]"
                : "border-gray-200 bg-white shadow-sm"
            }`}
          >
            {/* ================= TOOLBAR ================= */}
            <div
              className={`border-b p-4 ${
                isDarkMode
                  ? "border-[#30363D] bg-[#161B22]"
                  : "border-gray-200 bg-gray-50/70"
              }`}
            >
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
                {/* ================= CATEGORIES ================= */}
                <div
                  className={`inline-flex self-start rounded-lg p-1 ${
                    isDarkMode ? "bg-[#0D1117]" : "bg-gray-100"
                  }`}
                >
                  {[
                    {
                      value: "Tous",
                      label: "Tous",
                      icon: "fa-inbox",
                    },
                    {
                      value: "Individuel",
                      label: "Messages",
                      icon: "fa-envelope",
                    },
                    {
                      value: "Groupe",
                      label: "Communiqués",
                      icon: "fa-bullhorn",
                    },
                  ].map((category) => (
                    <button
                      key={category.value}
                      onClick={() => setSelectedCategory(category.value)}
                      className={`flex items-center gap-2 rounded-md px-3 py-2 text-[11px] font-medium transition ${
                        selectedCategory === category.value
                          ? isDarkMode
                            ? "bg-[#21262D] text-white shadow-sm"
                            : "bg-white text-gray-900 shadow-sm"
                          : isDarkMode
                            ? "text-gray-500 hover:text-gray-300"
                            : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <i className={`fas ${category.icon} text-[10px]`} />
                      {category.label}
                    </button>
                  ))}
                </div>

                {/* ================= SEARCH + FILTERS ================= */}
                <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap xl:justify-end">
                  {/* Search */}
                  <div className="relative flex-1 xl:max-w-sm">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500" />

                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      type="text"
                      placeholder="Rechercher un message..."
                      className={`h-9 w-full rounded-lg border pl-9 pr-3 text-xs outline-none transition ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] text-gray-200 placeholder:text-gray-600 focus:border-blue-500"
                          : "border-gray-200 bg-white text-gray-700 placeholder:text-gray-400 focus:border-blue-500"
                      }`}
                    />
                  </div>

                  {/* Type */}
                  <div className="relative">
                    <i className="fas fa-paper-plane absolute left-3 top-1/2 -translate-y-1/2 text-[9px] text-gray-500" />

                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className={`h-9 min-w-[135px] appearance-none rounded-lg border pl-8 pr-8 text-xs outline-none transition ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] text-gray-300 focus:border-blue-500"
                          : "border-gray-200 bg-white text-gray-600 focus:border-blue-500"
                      }`}
                    >
                      <option value="Tous les types">Tous les types</option>
                      <option value="Email">Emails</option>
                      <option value="SMS">SMS</option>
                    </select>

                    <i className="fas fa-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[8px] text-gray-500" />
                  </div>

                  {/* Destinataire */}
                  <div className="relative">
                    <i className="fas fa-users absolute left-3 top-1/2 -translate-y-1/2 text-[9px] text-gray-500" />

                    <select
                      value={selectedDestinateur}
                      onChange={(e) => setSelectedDestinateur(e.target.value)}
                      className={`h-9 min-w-[160px] appearance-none rounded-lg border pl-8 pr-8 text-xs outline-none transition ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] text-gray-300 focus:border-blue-500"
                          : "border-gray-200 bg-white text-gray-600 focus:border-blue-500"
                      }`}
                    >
                      <option value="Tous les destinateurs">
                        Tous les destinataires
                      </option>
                      <option value="Parent">Parents</option>
                      <option value="Professeur">Professeurs</option>
                    </select>

                    <i className="fas fa-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[8px] text-gray-500" />
                  </div>

                  {/* Statut */}
                  {/* <div className="relative">
                    <i className="fas fa-circle-check absolute left-3 top-1/2 -translate-y-1/2 text-[9px] text-gray-500" />

                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className={`h-9 min-w-[145px] appearance-none rounded-lg border pl-8 pr-8 text-xs outline-none transition ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] text-gray-300 focus:border-blue-500"
                          : "border-gray-200 bg-white text-gray-600 focus:border-blue-500"
                      }`}
                    >
                      <option value="Tous les statuts">Tous les statuts</option>
                      <option value="Envoyé">Envoyé</option>
                      <option value="Brouillon">Brouillon</option>
                    </select>

                    <i className="fas fa-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[8px] text-gray-500" />
                  </div> */}

                  {/* Reset */}
                  <button
                    onClick={handleResetFilter}
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                      isDarkMode
                        ? "border-[#30363D] bg-[#0D1117] text-gray-400 hover:bg-[#21262D] hover:text-gray-200"
                        : "border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    }`}
                    title="Réinitialiser les filtres"
                  >
                    <i className="fas fa-rotate-left text-[10px]" />
                  </button>
                </div>
              </div>
            </div>

            {/* ================= MESSAGE LIST ================= */}
            <div className="max-h-[600px] overflow-y-auto">
              {currentMessages.length > 0 ? (
                currentMessages.map((communication, index) => {
                  return (
                    <div
                      key={index}
                      className={`group border-b p-4 transition-colors last:border-b-0 sm:p-5 ${
                        isDarkMode
                          ? "border-[#21262D] hover:bg-[#161B22]"
                          : "border-gray-100 hover:bg-gray-50/70"
                      }`}
                    >
                      {/* ================= MESSAGE HEADER ================= */}
                      <div className="flex items-start gap-3">
                        {/* Message icon */}
                        <div
                          className={`hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg md:flex ${
                            communication.classe === "Groupe"
                              ? isDarkMode
                                ? "bg-purple-500/10 text-purple-400"
                                : "bg-purple-50 text-purple-600"
                              : isDarkMode
                                ? "bg-blue-500/10 text-blue-400"
                                : "bg-blue-50 text-blue-600"
                          }`}
                        >
                          <i
                            className={`fas ${
                              communication.classe === "Groupe"
                                ? "fa-bullhorn"
                                : "fa-envelope"
                            } text-[11px]`}
                          />
                        </div>

                        {/* Message content */}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              {/* Title + status */}
                              <div className="flex flex-wrap items-center gap-2">
                                <p
                                  className={`truncate text-xs font-semibold sm:text-[13px] ${
                                    isDarkMode
                                      ? "text-gray-200"
                                      : "text-gray-800"
                                  }`}
                                >
                                  {communication.type} - {communication.objet}
                                </p>

                                <span
                                  className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[9px] font-semibold ${
                                    communication.statut === "Envoyé"
                                      ? isDarkMode
                                        ? "bg-emerald-500/10 text-emerald-400"
                                        : "bg-emerald-50 text-emerald-600"
                                      : isDarkMode
                                        ? "bg-amber-500/10 text-amber-400"
                                        : "bg-amber-50 text-amber-600"
                                  }`}
                                >
                                  <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                      communication.statut === "Envoyé"
                                        ? "bg-emerald-500"
                                        : "bg-amber-500"
                                    }`}
                                  />

                                  {communication.statut}
                                </span>
                              </div>

                              {/* Recipient */}
                              <div className="mt-1.5 flex items-center gap-1.5">
                                <i className="fas fa-user text-[8px] text-gray-500" />

                                <p
                                  className={`text-[10px] ${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-500"
                                  }`}
                                >
                                  À:{" "}
                                  <span
                                    className={
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-600"
                                    }
                                  >
                                    {communication.classe === "Individuel"
                                      ? communication.destinataireNom ||
                                        "Destinataire inconnu"
                                      : communication.destinataires === "Parent"
                                        ? "Ensemble de parents"
                                        : "Ensemble des personnels"}

                                    {communication.classe === "Individuel" &&
                                      communication.destinataireContact && (
                                        <span className="ml-1.5 text-gray-500">
                                          • {communication.destinataireContact}
                                        </span>
                                      )}
                                  </span>
                                </p>
                              </div>
                            </div>

                            {/* Communication type */}
                            <div
                              className={`hidden shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[9px] font-medium lg:flex ${
                                isDarkMode
                                  ? "border-[#30363D] bg-[#0D1117] text-gray-400"
                                  : "border-gray-200 bg-white text-gray-500"
                              }`}
                            >
                              <i
                                className={`fas ${
                                  communication.type === "SMS"
                                    ? "fa-comment-sms"
                                    : "fa-envelope"
                                } text-[8px]`}
                              />

                              {communication.type}
                            </div>
                          </div>

                          {/* ================= MESSAGE BODY ================= */}
                          <div
                            className={`mt-3 rounded-lg border px-3.5 py-3 ${
                              isDarkMode
                                ? "border-[#21262D] bg-[#0D1117]/50"
                                : "border-gray-100 bg-gray-50/60"
                            }`}
                          >
                            <p
                              className={`text-[11px] leading-relaxed ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {communication.contenu.length > 200
                                ? communication.contenu.substring(0, 200) +
                                  "..."
                                : communication.contenu}
                            </p>
                          </div>

                          {/* ================= MESSAGE FOOTER ================= */}
                          <div
                            className={`mt-3 flex flex-col gap-3 border-t pt-3 sm:flex-row sm:items-center sm:justify-between ${
                              isDarkMode
                                ? "border-[#21262D]"
                                : "border-gray-100"
                            }`}
                          >
                            {/* Date */}
                            <div
                              className={`flex items-center gap-2 text-[10px] ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              <i className="far fa-clock text-[9px]" />

                              <span>
                                {communication.date} {communication.heure}
                              </span>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              {/* Delete */}
                              <button
                                className={`flex h-8 items-center gap-2 rounded-lg border px-3 text-[10px] font-medium transition-colors ${
                                  isDarkMode
                                    ? "border-[#30363D] text-gray-400 hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400"
                                    : "border-gray-200 text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                                }`}
                                onClick={() => {
                                  // console.log(communication);
                                  setMessageToDelete(communication);
                                  setShowDeleteModal(true);
                                }}
                              >
                                <i className="fas fa-trash text-[9px]" />
                                <span className="hidden sm:inline">
                                  Supprimer
                                </span>
                              </button>

                              {/* View */}
                              <button
                                className={`flex h-8 items-center gap-2 rounded-lg border px-3 text-[10px] font-medium transition-colors ${
                                  isDarkMode
                                    ? "border-blue-500/20 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                                    : "border-blue-100 bg-blue-50 text-blue-600 hover:bg-blue-100"
                                }`}
                                onClick={() => {
                                  setSelectedMessage(communication);
                                  setShowViewMessageModal(true);
                                }}
                              >
                                <i className="fas fa-eye text-[9px]" />
                                <span>Détails</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                /* ================= EMPTY STATE ================= */
                <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                  <div
                    className={`mb-3 flex h-11 w-11 items-center justify-center rounded-xl ${
                      isDarkMode
                        ? "bg-[#161B22] text-gray-500"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <i className="fas fa-inbox text-sm" />
                  </div>

                  <p
                    className={`text-xs font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Aucun message trouvé
                  </p>

                  <p className="mt-1 max-w-xs text-[10px] leading-relaxed text-gray-500">
                    Aucun message ne correspond actuellement aux critères de
                    recherche sélectionnés.
                  </p>
                </div>
              )}
            </div>

            {/* ================= LIST FOOTER ================= */}
            {currentMessages.length > 0 && (
              <div
                className={`flex items-center justify-between border-t px-4 py-2.5 ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#161B22]/60"
                    : "border-gray-200 bg-gray-50/60"
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className="fas fa-envelope text-[9px] text-gray-500" />

                  <span className="text-[9px] text-gray-500">
                    {filteredCommunications.length} message
                    {filteredCommunications.length > 1 ? "s" : ""}
                  </span>
                </div>

                <span className="text-[9px] text-gray-500">
                  Page {currentPage} sur {totalPages}
                </span>
              </div>
            )}
          </div>

          {/* ================= PAGINATION ================= */}
          {totalPages > 0 && (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {/* Information */}
              <div
                className={`text-[10px] ${
                  isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
              >
                Affichage de{" "}
                <span
                  className={`font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {indexOfFirstMessage + 1}
                </span>{" "}
                à{" "}
                <span
                  className={`font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {Math.min(indexOfLastMessage, filteredCommunications.length)}
                </span>{" "}
                sur{" "}
                <span
                  className={`font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {filteredCommunications.length}
                </span>{" "}
                messages
              </div>

              {/* Pagination controls */}
              <div className="flex items-center gap-1.5">
                {/* Previous */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-colors ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#161B22] text-gray-400 hover:bg-[#21262D]"
                      : "border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                  } ${
                    currentPage === 1 ? "cursor-not-allowed opacity-40" : ""
                  }`}
                >
                  <i className="fas fa-chevron-left text-[8px]" />
                </button>

                {/* Pages */}
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-[10px] font-medium transition-colors ${
                      currentPage === index + 1
                        ? "bg-blue-600 text-white shadow-sm"
                        : isDarkMode
                          ? "border border-[#30363D] bg-[#161B22] text-gray-400 hover:bg-[#21262D]"
                          : "border border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}

                {/* Next */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-colors ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#161B22] text-gray-400 hover:bg-[#21262D]"
                      : "border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                  } ${
                    currentPage === totalPages
                      ? "cursor-not-allowed opacity-40"
                      : ""
                  }`}
                >
                  <i className="fas fa-chevron-right text-[8px]" />
                </button>
              </div>
            </div>
          )}
        </main>
      )}

      {/* ================= VIEW MESSAGE MODAL ================= */}
      {showViewMessageModal && selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/65 backdrop-blur-sm p-4">
          {/* Overlay */}
          <div
            className="absolute inset-0"
            onClick={() => setShowViewMessageModal(false)}
          />

          {/* Modal */}
          <div
            className={`relative w-full max-w-2xl overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode
                ? "border-[#30363D] bg-[#161B22]"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* ================= HEADER ================= */}
            <div
              className={`flex items-center justify-between gap-4 border-b px-5 py-3.5 ${
                isDarkMode ? "border-[#30363D]" : "border-gray-200"
              }`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <i className="fas fa-envelope-open-text text-xs" />
                </div>

                <div className="min-w-0">
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    Détails du message
                  </h3>

                  <p className="mt-0.5 text-[9px] text-gray-500">
                    Informations complètes de la communication
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowViewMessageModal(false)}
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] text-gray-400 hover:bg-[#21262D] hover:text-white"
                    : "border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}
                title="Fermer"
              >
                <i className="fas fa-times text-[10px]" />
              </button>
            </div>

            {/* ================= CONTENT ================= */}
            <div className="space-y-3 p-4 sm:px-5">
              {/* Objet + type */}
              <div
                className={`flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#0D1117]"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div className="min-w-0">
                  <p className="text-[9px] font-medium uppercase tracking-wide text-gray-500">
                    Objet
                  </p>

                  <h4
                    className={`mt-1 text-xs font-semibold sm:text-sm ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {selectedMessage.objet}
                  </h4>

                  <div className="mt-1.5 flex items-center gap-1.5 text-[9px] text-gray-500">
                    <i className="far fa-clock text-[8px]" />

                    <span>
                      {selectedMessage.date} à {selectedMessage.heure}
                    </span>
                  </div>
                </div>

                {/* Type */}
                <span
                  className={`inline-flex h-7 shrink-0 self-start items-center gap-1.5 rounded-full px-2.5 text-[9px] font-semibold sm:self-auto ${
                    selectedMessage.type === "SMS"
                      ? isDarkMode
                        ? "bg-purple-500/10 text-purple-400"
                        : "bg-purple-50 text-purple-600"
                      : isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <i
                    className={`fas ${
                      selectedMessage.type === "SMS"
                        ? "fa-comment-sms"
                        : "fa-envelope"
                    } text-[8px]`}
                  />

                  {selectedMessage.type}
                </span>
              </div>

              {/* Destinataire + diffusion */}
              <div
                className={`grid grid-cols-1 overflow-hidden rounded-xl border sm:grid-cols-2 ${
                  isDarkMode ? "border-[#30363D]" : "border-gray-200"
                }`}
              >
                {/* Destinataire */}
                <div
                  className={`flex items-center gap-3 px-4 py-3 sm:border-r ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-50 text-blue-600"
                    }`}
                  >
                    <i className="fas fa-user text-[10px]" />
                  </div>

                  <div className="min-w-0">
                    <p className="text-[8px] font-medium uppercase tracking-wide text-gray-500">
                      Destinataire
                    </p>

                    <p
                      className={`mt-0.5 truncate text-[11px] font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      {selectedMessage.classe === "Individuel"
                        ? selectedMessage.destinataireNom ||
                          "Destinataire inconnu"
                        : selectedMessage.destinataires === "Parent"
                          ? "Ensemble de parents"
                          : "Ensemble des personnels"}
                    </p>

                    {selectedMessage.classe === "Individuel" &&
                      selectedMessage.destinataireContact && (
                        <div className="mt-1 flex items-center gap-1.5">
                          <i
                            className={`fas ${
                              selectedMessage.type === "SMS"
                                ? "fa-phone"
                                : "fa-envelope"
                            } text-[8px] text-gray-500`}
                          />

                          <span className="truncate text-[9px] text-gray-500">
                            {selectedMessage.destinataireContact}
                          </span>
                        </div>
                      )}
                  </div>
                </div>

                {/* Diffusion */}
                <div
                  className={`flex items-center gap-3 border-t px-4 py-3 sm:border-t-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-purple-500/10 text-purple-400"
                        : "bg-purple-50 text-purple-600"
                    }`}
                  >
                    <i className="fas fa-bullhorn text-[10px]" />
                  </div>

                  <div>
                    <p className="text-[8px] font-medium uppercase tracking-wide text-gray-500">
                      Type de diffusion
                    </p>

                    <p
                      className={`mt-0.5 text-[11px] font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      {selectedMessage.classe}
                    </p>
                  </div>
                </div>
              </div>

              {/* ================= MESSAGE ================= */}
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-[#21262D] text-gray-400"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <i className="fas fa-align-left text-[9px]" />
                  </div>

                  <p
                    className={`text-[10px] font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Contenu du message
                  </p>
                </div>

                <div
                  className={`max-h-[280px] overflow-y-auto whitespace-pre-wrap rounded-xl border p-4 text-[11px] leading-relaxed ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#0D1117] text-gray-300"
                      : "border-gray-200 bg-gray-50 text-gray-600"
                  }`}
                >
                  {selectedMessage.contenu}
                </div>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div
              className={`flex justify-end border-t px-5 py-3 ${
                isDarkMode
                  ? "border-[#30363D] bg-[#0D1117]/40"
                  : "border-gray-200 bg-gray-50/70"
              }`}
            >
              <button
                onClick={() => setShowViewMessageModal(false)}
                className={`flex h-9 items-center justify-center gap-2 rounded-lg border px-4 text-xs font-medium transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                <i className="fas fa-times text-[9px]" />
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {/* ================= DELETE CONFIRMATION MODAL ================= */}
      {showDeleteModal && messageToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/65 backdrop-blur-sm p-4">
          <div
            className={`relative w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode
                ? "border-[#30363D] bg-[#161B22]"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* ================= HEADER ================= */}
            <div
              className={`flex items-center justify-between gap-4 border-b px-5 py-3.5 ${
                isDarkMode ? "border-[#30363D]" : "border-gray-200"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isDarkMode
                      ? "bg-red-500/10 text-red-400"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  <i className="fas fa-trash-alt text-xs" />
                </div>

                <div>
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    Confirmer la suppression
                  </h3>

                  <p className="mt-0.5 text-[9px] text-gray-500">
                    Cette opération est irréversible
                  </p>
                </div>
              </div>

              <button
                type="button"
                disabled={mutationDeleteMessage.isPending}
                onClick={() => setShowDeleteModal(false)}
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] text-gray-400 hover:bg-[#21262D] hover:text-white"
                    : "border-gray-200 text-gray-500 hover:bg-gray-100"
                } ${
                  mutationDeleteMessage.isPending
                    ? "cursor-not-allowed opacity-50"
                    : ""
                }`}
              >
                <i className="fas fa-times text-[10px]" />
              </button>
            </div>

            {/* ================= CONTENT ================= */}
            <div className="p-5">
              <div
                className={`flex items-start gap-3 rounded-xl border p-4 ${
                  isDarkMode
                    ? "border-red-500/20 bg-red-500/[0.05]"
                    : "border-red-100 bg-red-50/60"
                }`}
              >
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isDarkMode
                      ? "bg-red-500/10 text-red-400"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  <i className="fas fa-exclamation-triangle text-xs" />
                </div>

                <div>
                  <p
                    className={`text-xs font-semibold ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    Supprimer ce message ?
                  </p>

                  <p
                    className={`mt-1.5 text-[11px] leading-relaxed ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Êtes-vous sûr de vouloir supprimer ce message ? Cette action
                    est irréversible.
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 px-1">
                <i className="fas fa-info-circle text-[9px] text-red-500" />

                <p className="text-[9px] text-gray-500">
                  Le message supprimé ne pourra pas être restauré.
                </p>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div
              className={`flex flex-col-reverse gap-2 border-t px-5 py-3.5 sm:flex-row sm:justify-end ${
                isDarkMode
                  ? "border-[#30363D] bg-[#0D1117]/40"
                  : "border-gray-200 bg-gray-50/70"
              }`}
            >
              <button
                onClick={() => setShowDeleteModal(false)}
                className={`flex h-10 items-center justify-center gap-2 rounded-lg border px-4 text-xs font-medium transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                <i className="fas fa-times text-[9px]" />
                Annuler
              </button>

              <button
                disabled={mutationDeleteMessage.isPending}
                onClick={() => {
                  handleDeleteMessage();
                  setShowDeleteModal(false);
                }}
                className={`flex h-10 items-center justify-center gap-2 rounded-lg bg-red-600 px-5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700 ${
                  mutationDeleteMessage.isPending
                    ? "cursor-not-allowed opacity-60"
                    : ""
                }`}
              >
                {mutationDeleteMessage.isPending ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin text-[9px]" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash-alt text-[9px]" />
                    Confirmer la suppression
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/65 backdrop-blur-sm p-4">
          <div
            className={`relative flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode
                ? "border-[#30363D] bg-[#161B22]"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* ================= HEADER ================= */}
            <div
              className={`flex shrink-0 items-center justify-between gap-4 border-b px-5 py-3.5 ${
                isDarkMode ? "border-[#30363D]" : "border-gray-200"
              }`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <i className="fas fa-paper-plane text-xs" />
                </div>

                <div className="min-w-0">
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    Nouveau communiqué
                  </h3>

                  <p className="mt-0.5 text-[9px] text-gray-500">
                    Composez et envoyez une communication aux parents ou au
                    personnel
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowNewMessageModal(false);
                  resetCreateMessageForm();
                }}
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition ${
                  isDarkMode
                    ? "border-[#30363D] text-gray-400 hover:bg-[#21262D] hover:text-white"
                    : "border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}
                title="Fermer"
              >
                <i className="fas fa-times text-[10px]" />
              </button>
            </div>

            {/* ================= CONTENT ================= */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-5">
                {/* ==================================================
              1. CIBLE + CANAL
          ================================================== */}
                <section>
                  <div className="mb-2.5 flex items-center gap-2">
                    <div
                      className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                        isDarkMode
                          ? "bg-[#21262D] text-gray-400"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <i className="fas fa-users text-[9px]" />
                    </div>

                    <div>
                      <p
                        className={`text-[11px] font-semibold ${
                          isDarkMode ? "text-gray-200" : "text-gray-800"
                        }`}
                      >
                        Cible du message
                      </p>
                      <p className="text-[8px] text-gray-500">
                        Choisissez les personnes concernées par cette
                        communication
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_180px]">
                    {/* Type destinataire */}
                    <div
                      className={`grid grid-cols-2 gap-1.5 rounded-xl border p-1.5 sm:grid-cols-4 ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117]"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      {/* Parents par classe */}
                      <label
                        htmlFor="edit-type-normal"
                        className={`relative flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 transition ${
                          createMessageForm.type_destinateur === "parents_class"
                            ? isDarkMode
                              ? "border-blue-500/40 bg-blue-500/10 text-blue-400"
                              : "border-blue-200 bg-blue-50 text-blue-700"
                            : isDarkMode
                              ? "border-transparent text-gray-400 hover:bg-[#21262D]"
                              : "border-transparent text-gray-600 hover:bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          id="edit-type-normal"
                          name="edit-message-type"
                          value="parents_class"
                          className="sr-only"
                          defaultChecked={
                            createMessageForm.type_destinateur ===
                            "parents_class"
                          }
                          onChange={(e) => {
                            setcreateMessageForm((prev) => ({
                              ...prev,
                              type_destinateur: e.target.value,
                              destinateur_id: "",
                            }));
                          }}
                        />

                        <div
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                            createMessageForm.type_destinateur ===
                            "parents_class"
                              ? isDarkMode
                                ? "bg-blue-500/15"
                                : "bg-white"
                              : isDarkMode
                                ? "bg-[#21262D]"
                                : "bg-white"
                          }`}
                        >
                          <i className="fas fa-chalkboard text-[9px]" />
                        </div>

                        <span className="text-[10px] font-medium leading-tight">
                          Parents par classe
                        </span>
                      </label>

                      {/* Un parent */}
                      <label
                        htmlFor="edit-type-parent"
                        className={`relative flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 transition ${
                          createMessageForm.type_destinateur === "single_parent"
                            ? isDarkMode
                              ? "border-blue-500/40 bg-blue-500/10 text-blue-400"
                              : "border-blue-200 bg-blue-50 text-blue-700"
                            : isDarkMode
                              ? "border-transparent text-gray-400 hover:bg-[#21262D]"
                              : "border-transparent text-gray-600 hover:bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          id="edit-type-parent"
                          value="single_parent"
                          name="edit-message-type"
                          className="sr-only"
                          defaultChecked={
                            createMessageForm.type_destinateur ===
                            "single_parent"
                          }
                          onChange={handleChangeDestinateur}
                        />

                        <div
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                            createMessageForm.type_destinateur ===
                            "single_parent"
                              ? isDarkMode
                                ? "bg-blue-500/15"
                                : "bg-white"
                              : isDarkMode
                                ? "bg-[#21262D]"
                                : "bg-white"
                          }`}
                        >
                          <i className="fas fa-user text-[9px]" />
                        </div>

                        <span className="text-[10px] font-medium">
                          Un parent
                        </span>
                      </label>

                      {/* Tous personnels */}
                      <label
                        htmlFor="edit-type-personals"
                        className={`relative flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 transition ${
                          createMessageForm.type_destinateur === "personals"
                            ? isDarkMode
                              ? "border-purple-500/40 bg-purple-500/10 text-purple-400"
                              : "border-purple-200 bg-purple-50 text-purple-700"
                            : isDarkMode
                              ? "border-transparent text-gray-400 hover:bg-[#21262D]"
                              : "border-transparent text-gray-600 hover:bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          id="edit-type-personals"
                          value="personals"
                          name="edit-message-type"
                          className="sr-only"
                          defaultChecked={
                            createMessageForm.type_destinateur === "personals"
                          }
                          onChange={handleChangeDestinateur}
                        />

                        <div
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                            createMessageForm.type_destinateur === "personals"
                              ? isDarkMode
                                ? "bg-purple-500/15"
                                : "bg-white"
                              : isDarkMode
                                ? "bg-[#21262D]"
                                : "bg-white"
                          }`}
                        >
                          <i className="fas fa-users text-[9px]" />
                        </div>

                        <span className="text-[10px] font-medium leading-tight">
                          Tous les personnels
                        </span>
                      </label>

                      {/* Un personnel */}
                      <label
                        htmlFor="edit-type-personal"
                        className={`relative flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 transition ${
                          createMessageForm.type_destinateur ===
                          "single_personal"
                            ? isDarkMode
                              ? "border-purple-500/40 bg-purple-500/10 text-purple-400"
                              : "border-purple-200 bg-purple-50 text-purple-700"
                            : isDarkMode
                              ? "border-transparent text-gray-400 hover:bg-[#21262D]"
                              : "border-transparent text-gray-600 hover:bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          id="edit-type-personal"
                          value="single_personal"
                          name="edit-message-type"
                          className="sr-only"
                          defaultChecked={
                            createMessageForm.type_destinateur ===
                            "single_personal"
                          }
                          onChange={handleChangeDestinateur}
                        />

                        <div
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                            createMessageForm.type_destinateur ===
                            "single_personal"
                              ? isDarkMode
                                ? "bg-purple-500/15"
                                : "bg-white"
                              : isDarkMode
                                ? "bg-[#21262D]"
                                : "bg-white"
                          }`}
                        >
                          <i className="fas fa-user-tie text-[9px]" />
                        </div>

                        <span className="text-[10px] font-medium leading-tight">
                          Un personnel
                        </span>
                      </label>
                    </div>

                    {/* Mode d'envoi */}
                    <div>
                      <label
                        className={`mb-1.5 block text-[9px] font-medium uppercase tracking-wide ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Mode d'envoi
                      </label>

                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <i
                            className={`fas ${
                              createMessageForm.canal === "email"
                                ? "fa-envelope"
                                : "fa-comment-sms"
                            } text-[10px] text-gray-500`}
                          />
                        </div>

                        <select
                          value={createMessageForm.canal}
                          className={`h-10 w-full appearance-none rounded-lg border pl-9 pr-8 text-xs font-medium outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                            isDarkMode
                              ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                              : "border-gray-200 bg-white text-gray-700"
                          }`}
                          onChange={(e) => {
                            handleCanalChange(
                              e.target.value as "sms" | "email",
                            );
                          }}
                        >
                          <option value="email">Email</option>
                          <option value="sms">SMS</option>
                        </select>

                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                          <i className="fas fa-chevron-down text-[8px]" />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* ==================================================
              2. DESTINATAIRES
          ================================================== */}
                <section>
                  <label
                    className={`mb-1.5 flex items-center gap-2 text-[10px] font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <i className="fas fa-address-book text-[9px] text-gray-500" />
                    Destinataires
                  </label>

                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
                      <i className="fas fa-user-check text-[10px] text-gray-500" />
                    </div>

                    {createMessageForm.type_destinateur === "parents_class" && (
                      <select
                        className={`h-10 w-full appearance-none rounded-lg border pl-9 pr-9 text-xs outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                          isDarkMode
                            ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                            : "border-gray-200 bg-white text-gray-700"
                        }`}
                        onChange={(e) => {
                          setcreateMessageForm((prev) => ({
                            ...prev,
                            destinateur_id: e.target.value,
                          }));
                        }}
                      >
                        <option value="">Selectionner destinateur</option>

                        {classesArray.map((classe, index) => (
                          <option value={classe.id} key={index}>
                            {capitalizeFirstLetter(classe.designation)}
                          </option>
                        ))}
                      </select>
                    )}

                    {createMessageForm.type_destinateur === "single_parent" && (
                      <select
                        className={`h-10 w-full appearance-none rounded-lg border pl-9 pr-9 text-xs outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                          isDarkMode
                            ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                            : "border-gray-200 bg-white text-gray-700"
                        }`}
                        onChange={(e) => {
                          setcreateMessageForm((prev) => ({
                            ...prev,
                            destinateur_id: e.target.value,
                          }));
                        }}
                      >
                        <option value="">Selectionner destinateur</option>

                        {affectedStudents
                          .sort((a, b) => a?.nom.localeCompare(b?.nom))
                          .map((student, index) => {
                            return (
                              <option value={student.parentId} key={index}>
                                {student.parentNoms.toUpperCase()}
                                <p>- Parent de {student.noms}</p>
                              </option>
                            );
                          })}
                      </select>
                    )}

                    {createMessageForm.type_destinateur === "personals" && (
                      <select
                        className={`h-10 w-full appearance-none rounded-lg border pl-9 pr-9 text-xs outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                          isDarkMode
                            ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                            : "border-gray-200 bg-white text-gray-700"
                        }`}
                        onChange={(e) => {
                          setcreateMessageForm((prev) => ({
                            ...prev,
                            destinateur_id: e.target.value,
                          }));
                        }}
                      >
                        <option value="">Selectionner destinateur</option>
                        <option value="all">Tous les personnels</option>
                      </select>
                    )}

                    {createMessageForm.type_destinateur ===
                      "single_personal" && (
                      <select
                        className={`h-10 w-full appearance-none rounded-lg border pl-9 pr-9 text-xs outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                          isDarkMode
                            ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                            : "border-gray-200 bg-white text-gray-700"
                        }`}
                        onChange={(e) => {
                          setcreateMessageForm((prev) => ({
                            ...prev,
                            destinateur_id: e.target.value,
                          }));
                        }}
                      >
                        <option value="">Selectionner destinateur</option>

                        {affectedPersonals.map((personal, index) => (
                          <option key={index} value={personal.personalId}>
                            {capitalizeFirstLetter(personal.noms)}
                          </option>
                        ))}
                      </select>
                    )}

                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                      <i className="fas fa-chevron-down text-[8px]" />
                    </div>
                  </div>
                </section>

                {/* ==================================================
              3. OBJET
          ================================================== */}
                <section>
                  <label
                    className={`mb-1.5 flex items-center gap-2 text-[10px] font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <i className="fas fa-heading text-[9px] text-gray-500" />
                    Objet
                  </label>

                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <i className="fas fa-tag text-[9px] text-gray-500" />
                    </div>

                    <input
                      type="text"
                      className={`h-10 w-full rounded-lg border pl-9 pr-3 text-xs outline-none transition placeholder:text-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                          : "border-gray-200 bg-white text-gray-700"
                      }`}
                      placeholder="Objet du message"
                      value={createMessageForm.objet}
                      onChange={(e) =>
                        setcreateMessageForm((prev) => ({
                          ...prev,
                          objet: e.target.value,
                        }))
                      }
                    />
                  </div>
                </section>

                {/* ==================================================
              4. CONTENU
          ================================================== */}
                <section>
                  <div className="mb-1.5 flex items-center justify-between gap-4">
                    <label
                      className={`flex items-center gap-2 text-[10px] font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <i className="fas fa-align-left text-[9px] text-gray-500" />
                      Contenu du message
                    </label>

                    {createMessageForm.canal === "sms" && (
                      <div
                        className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-medium ${
                          createMessageForm.contenu.length >= 300
                            ? isDarkMode
                              ? "bg-amber-500/10 text-amber-400"
                              : "bg-amber-50 text-amber-600"
                            : isDarkMode
                              ? "bg-[#21262D] text-gray-400"
                              : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        <i className="fas fa-font text-[8px]" />

                        <span id="smsCharCount">
                          {createMessageForm.contenu.length}
                        </span>

                        <span>/ 320 caractères (SMS)</span>
                      </div>
                    )}
                  </div>

                  <div
                    className={`overflow-hidden rounded-xl border transition focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/10 ${
                      isDarkMode
                        ? "border-[#30363D] bg-[#0D1117]"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    {/* Toolbar Email */}
                    {createMessageForm.canal === "email" && (
                      <div
                        className={`flex h-9 items-center gap-1 border-b px-2 ${
                          isDarkMode
                            ? "border-[#30363D] bg-[#161B22]"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      >
                        <button
                          type="button"
                          disabled
                          title="Italique (bientôt disponible)"
                          className={toolbarButtonClass}
                        >
                          <i className="fas fa-italic text-xs" />
                        </button>

                        <button
                          type="button"
                          disabled
                          title="Souligné (bientôt disponible)"
                          className={toolbarButtonClass}
                        >
                          <i className="fas fa-underline text-xs" />
                        </button>

                        <span
                          className={`mx-1 h-4 w-px ${
                            isDarkMode ? "bg-[#30363D]" : "bg-gray-300"
                          }`}
                        />

                        <button
                          type="button"
                          disabled
                          title="Liste à puces (bientôt disponible)"
                          className={toolbarButtonClass}
                        >
                          <i className="fas fa-list-ul text-xs" />
                        </button>

                        <div className="ml-auto flex items-center gap-1.5 pr-1 text-[8px] text-gray-500">
                          <i className="fas fa-envelope text-[8px]" />
                          Email
                        </div>
                      </div>
                    )}

                    <textarea
                      placeholder="Ecrire..."
                      className={`min-h-[130px] w-full resize-y border-none px-3.5 py-3 text-xs leading-relaxed outline-none ${
                        isDarkMode
                          ? "bg-[#0D1117] text-gray-200 placeholder:text-gray-600"
                          : "bg-white text-gray-700 placeholder:text-gray-400"
                      }`}
                      rows={5}
                      value={createMessageForm.contenu}
                      onChange={(e) => {
                        if (e.target.value.length > 320) {
                          setcreateMessageForm((prev) => ({
                            ...prev,
                            contenu: e.target.value.substring(0, 320),
                          }));
                        } else {
                          setcreateMessageForm((prev) => ({
                            ...prev,
                            contenu: e.target.value,
                          }));
                        }
                      }}
                    />
                  </div>

                  <div className="mt-1.5 flex items-center gap-1.5 px-1">
                    <i className="fas fa-info-circle text-[8px] text-gray-500" />

                    <p className="text-[8px] text-gray-500">
                      {createMessageForm.canal === "sms"
                        ? "Le contenu SMS est limité à 320 caractères."
                        : "Rédigez clairement le contenu de votre communication."}
                    </p>
                  </div>
                </section>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div
              className={`flex shrink-0 flex-col-reverse gap-2 border-t px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between ${
                isDarkMode
                  ? "border-[#30363D] bg-[#0D1117]/40"
                  : "border-gray-200 bg-gray-50/70"
              }`}
            >
              {/* Indication */}
              <div className="hidden items-center gap-2 sm:flex">
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-md ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <i className="fas fa-info text-[8px]" />
                </div>

                <p className="text-[8px] text-gray-500">
                  Vérifiez le destinataire et le contenu avant l'envoi.
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => {
                    setShowNewMessageModal(false);
                    resetCreateMessageForm();
                  }}
                  className={`flex h-9 items-center justify-center gap-2 rounded-lg border px-4 text-xs font-medium transition ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                      : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <i className="fas fa-times text-[9px]" />
                  Annuler
                </button>

                <button
                  disabled={isPending}
                  onClick={handleSendMessage}
                  className={`flex h-9 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 ${
                    isPending ? "cursor-not-allowed opacity-70" : ""
                  }`}
                >
                  {isPending ? (
                    <>
                      <i className="fas fa-spinner fa-spin text-[9px]" />
                      Envoie en cours...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane text-[9px]" />
                      Envoyer message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <MyLoader
        isLoading={mutationDeleteMessage.isPending}
        message="Suppression en cours..."
      />

      {/* ===== NOTIFICATION ===== */}
      {notification && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg z-[60] text-white shadow-lg transition-all duration-500 flex items-center gap-2 ${
            notification.type === "success" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          <i
            className={`fas ${
              notification.type === "success"
                ? "fa-check-circle"
                : "fa-circle-exclamation"
            }`}
          ></i>
          {notification.message}
        </div>
      )}
    </div>
  );
};
export default Message;
